/**
 * chat-panel.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CollaborationService } from "./collaboration.service";

/**
 * The {@link ChatPanelComponent} creates a small panel that links to the
 * {@link CollaborationService} and displays all chat messages, as well as a
 * small input for sending your own.
 */
@Component({
  selector: "chat-panel",
  template: `
  <div
    class="panel panel-default fade"
    [class.in]="!hideWhenEmpty || users.length"
    (click)="updateReadCount()">
    <div class="panel-heading" (click)="panelOpen = !panelOpen">
      <h3 class="panel-title">
        Chat
        <span class="badge">{{unreadCount || 0}} unread</span>
      </h3>
    </div>
    <div class="panel-body collapse" [class.in]="panelOpen">
      <div class="chat-window" #chatWindow>
        <p *ngFor="let chat of collaboration.chatMessages" class="report">
          <strong>
            {{chat.author}}
            {{chat.group ? "[" + chat.group + "]" : ""}}
            {{chat.action ? chat.action + "." : ":"}}
          </strong>
          {{chat.message}}
        </p>
        <em
          *ngIf="collaboration.chatMessages.length === 0"
          class="notification">
          No chats since you joined the session.
        </em>
        <em *ngIf="!collaboration.chatEnabled" class="notification">
          Chat is unavailable.
        </em>
      </div>
    </div>
    <form [formGroup]="form" class="collapse" [class.in]="panelOpen">
      <div class="input-group new-chat">
        <input
          #chatInput
          type="text"
          class="form-control"
          formControlName="newChat"
          placeholder="Message...">
        <span class="input-group-btn">
          <button
            class="btn btn-primary"
            (click)="sendChat()">
            Send
          </button>
        </span>
      </div>
    </form>
  </div>
`,
  styles: [
    `
    .panel-body {
      padding: 0;
    }
    .chat-window {
      height: 250px;
      overflow-y: scroll;
      position: relative;
    }
    .chat-window p {
      padding-left: 5px;
      padding-right: 2px;
    }
    .chat-window .notification {
      text-align: center;
      width: 100%;
      position: absolute;
      top: 120px;
    }
    form {
      margin-left: -1px;
      margin-right: -1px;
    }
    .new-chat input {
      border-top-left-radius: 0;
    }
    .new-chat .btn {
      border-top-right-radius: 0;
    }
    `
  ]
})
export class ChatPanelComponent implements OnInit
{
  /**
   * Whether the panel should initially be open.
   */
  @Input()
  public startOpen : boolean;

  /**
   * Whether the panel should open when some sort of changes occur. NOTE: This
   * is duplicated from another panel and not currently hooked up to the chat
   * mechanisms, so enabling it WILL NOT YET open the panel after chat events.
   */
  @Input()
  public openOnChanges : boolean = false;

  /**
   * Whether the body of the panel is open or collapsed.
   */
  public panelOpen : boolean = this.startOpen || false;

  /**
   * Reference to the DOM element for the chat window itself, for the purposes
   * of being able to enforce scroll position as messages come in.
   */
  @ViewChild("chatWindow") private chatWindowRef : ElementRef;

  /**
   * Number of milliseconds after an event to scroll to the bottom.
   */
  private scrollDelay : number = 100;

  /**
   * The control that holds new chat input before being sent.
   */
  public newChat : FormControl = new FormControl("", Validators.required);

  /**
   * The overall form that contains the new chat control. This is really only
   * necessary because I've gone and made a <form> element, which needs a group
   * in the reactive forms module. I could delete it but maybe it'll be useful.
   */
  public form : FormGroup = new FormGroup({newChat: this.newChat});

  /**
   * The number of messages present ("read") when the user last interacted with
   * the chat window.
   */
  public readCount : number = 0;

  /**
   * The number of messages that have arrived since the user last acknowledged
   * them, determined from the total and the number at last interaction.
   */
  public get unreadCount () : number
  {
  	// TODO: This was necessary because TLS saw "-13 unread" in the chat window,
  	// TODO: but it warrants a real solution.
    return Math.max(0, this.collaboration.chatMessages.length - this.readCount);
  }

  /**
   * Construct a new {@link CollaboratorPanelComponent}.
   *
   * @param messaging
   *        The {@link MessagingService}
   */
  constructor (
    private collaboration : CollaborationService
  )
  {
    /* Nothing. */
  }

  /**
   * Arrange to receive updates about chat updates. In our case, this simply
   * listens to the chat "poke" stream, and uses it to scroll the window.
   */
  public ngOnInit () : void
  {
    this.collaboration.chatPokes.delay(this.scrollDelay).subscribe(
      () => this.scrollToBottom());
  }

  /**
   * Send the chat message the user has typed to the server.
   */
  public sendChat () : void
  {
    if (this.newChat.valid)
    {
      this.updateReadCount();
      const chat : string = this.newChat.value;
      this.collaboration.sendChat(chat).subscribe((sent : boolean) => {
        this.updateReadCount();
        setTimeout(() => this.scrollToBottom(), this.scrollDelay);
        if (sent)
        {
          // clear the form field now that it has been sent
          this.newChat.reset("");
        }
        else
        {
          throw new Error("Could not send chat!");
        }
      });
    }
    else
    {
      console.warn("Attempted to send empty chat!");
    }
  }

  /**
   * Update the read count to be the current number of messages. Effectively
   * this "marks everything as read" and should be called whenever the user
   * interacts with the chat (more advanced flagging would be a very large leap
   * in complexity for not much value).
   */
  public updateReadCount () : void
  {
    this.readCount = this.collaboration.chatMessages.length;
  }

  /**
   * Scroll the chat window to the bottom of its contents.
   */
  public scrollToBottom () : void
  {
    this.chatWindowRef.nativeElement.scrollTop =
      this.chatWindowRef.nativeElement.scrollHeight;
  }

  @ViewChild("chatInput") private chatInput : ElementRef;

  public externalFocalize () : void
  {
    this.panelOpen = true;
    setTimeout(
      () => (this.chatInput.nativeElement as HTMLElement).focus(),
      100
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/chat-panel.component.ts